import { storage } from '@discovr/common';
import { setDraftStatus } from './draftStatus';
import type { UseFormGetFieldState } from 'react-hook-form';
import type { CampaignFormData, ProductRow } from './campaignSchema';
import { PRIMARY_GOALS, FIELD_LABELS, MAX_PRODUCTS } from './constants';

// Re-exported for existing consumers importing MAX_PRODUCTS from here —
// canonical definition moved to ./constants (see its comment for why).
export { MAX_PRODUCTS };

/* ── Form errors ─────────────────────────────────────────────── */

// Deepest message under an RHF error node — object fields nest their errors
// (e.g. usageRights.days.message), which a flat `.message` read misses.
export function fieldErrorMessage(node: unknown, depth = 0): string | undefined {
  if (depth > 4 || !node || typeof node !== 'object') return undefined;
  const record = node as Record<string, unknown>;
  if (typeof record.message === 'string' && record.message) return record.message;
  for (const [key, value] of Object.entries(record)) {
    if (key === 'ref' || key === 'type') continue;
    const found = fieldErrorMessage(value, depth + 1);
    if (found) return found;
  }
  return undefined;
}

// Names the first blocking field and its message for the "can't continue" toast.
// Reads through getFieldState so it reflects the trigger() that just ran, not a
// stale render's error proxy (which lags a click behind inside an async handler).
export function firstFieldErrorMessage(
  fields: (keyof CampaignFormData)[],
  getFieldState: UseFormGetFieldState<CampaignFormData>,
): string {
  for (const field of fields) {
    const { error } = getFieldState(field);
    if (!error) continue;
    const label = FIELD_LABELS[field] ?? field;
    const message = fieldErrorMessage(error);
    return message ? `${label}: ${message}` : `Please fill in ${label}.`;
  }
  return 'Please fill in the required fields before continuing.';
}

/* ── Draft persistence ───────────────────────────────────────── */

// The storage key is host-supplied (see NewCampaignWizardProps.draftKey) so
// drafts from different hosts — e.g. an admin creating for several brands —
// never overwrite each other.
export const DRAFT_SAVE_DEBOUNCE_MS = 400;

export function readStoredDraft(
  draftKey: string,
): { formData: Partial<CampaignFormData>; currentStep: number; maxReachedStep: number } | null {
  try {
    const raw = storage.get(draftKey);
    if (!raw) return null;
    const o = JSON.parse(raw) as { v?: number; formData?: unknown; currentStep?: unknown; maxReachedStep?: unknown };
    if (o.v !== 2 || !o.formData) return null;
    // Number.isFinite (not just typeof === 'number') so a persisted NaN can't
    // slip through — Math.max(NaN, …) stays NaN and would deaden every stepper
    // button (`step <= NaN` is always false) until the next persist self-heals.
    const step = Math.min(
      5,
      Math.max(1, typeof o.currentStep === 'number' && Number.isFinite(o.currentStep) ? o.currentStep : 1),
    );
    // Furthest step the user reached (persisted so a back-nav + reload doesn't
    // forget it). Falls back to the current step for pre-existing drafts; never
    // below the restored step, capped at the final step (6).
    const maxStep =
      typeof o.maxReachedStep === 'number' && Number.isFinite(o.maxReachedStep) ? o.maxReachedStep : step;
    return {
      formData: o.formData as Partial<CampaignFormData>,
      currentStep: step,
      maxReachedStep: Math.min(6, Math.max(step, maxStep)),
    };
  } catch {
    return null;
  }
}

export function writeStoredDraft(
  draftKey: string,
  formData: Partial<CampaignFormData>,
  currentStep: number,
  maxReachedStep: number = currentStep,
) {
  try {
    storage.set(draftKey, JSON.stringify({ v: 2, formData, currentStep, maxReachedStep }));
    setDraftStatus('saved');
  } catch {
    setDraftStatus('error');
  }
}

export function clearNewCampaignDraft(draftKey: string) {
  try {
    storage.remove(draftKey);
  } catch {
    /**/
  }
  setDraftStatus('idle');
}

/* ── Pure utility functions ──────────────────────────────────── */

export function goalLabel(primaryGoal: string, primaryGoalCustom = ''): string {
  const custom = primaryGoalCustom.trim();
  if (custom) return custom;
  return PRIMARY_GOALS.find((g) => g.value === primaryGoal)?.label || primaryGoal || '—';
}

export const toDateInputValue = (d: Date) =>
  `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;

// Empty array means "no filter". Strips the legacy 'Any' sentinel that stale
// drafts may still hold so the UI never round-trips it back to the BE.
export function normalizeMultiValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((v) => String(v).trim())
      .filter((v) => v && v.toLowerCase() !== 'any');
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed && trimmed.toLowerCase() !== 'any' ? [trimmed] : [];
  }
  return [];
}

export const parseBulletList = (text: string): string[] =>
  text.split('\n').map(s => s.replace(/^\s*\d+[.)]\s*/, '').trim()).filter(Boolean);

export const formatNumber = (value: string | number) => {
  if (!value) return '';
  const numValue = Number(String(value).replace(/[^0-9]/g, ''));
  return numValue.toLocaleString('en-IN');
};

export const unformatNumber = (value: string) => {
  return value.replace(/[^0-9]/g, '');
};

// empty selection renders as "All" — semantically equivalent to
// the legacy "Any" sentinel but no longer pollutes the wire payload.
export const selectionLabel = (vals: string[]) => {
  if (!vals.length) return 'All (no filter)';
  if (vals.length <= 2) return vals.join(', ');
  return `${vals.slice(0, 2).join(', ')} +${vals.length - 2}`;
};

/* ── Products editor ─────────────────────────────────────────── */

// Allow a new row only when under the cap and the last row already has a name,
// preventing endless empty rows. An empty list always gets its first row.
export function canAddProduct(items: ProductRow[], max = MAX_PRODUCTS): boolean {
  if (items.length >= max) return false;
  if (items.length === 0) return true;
  const last = items[items.length - 1];
  return (last?.name ?? '').trim().length > 0;
}

/** One product row's create-request shape (BE `domain.Product` field names). */
export interface ProductPayload {
  name: string;
  product_url: string;
  notes: string;
}

// Products sent on campaign create: trims every field, and drops any row
// whose (trimmed) name is empty — a name-less row is "no product".
export function buildProductsPayload(rows: ProductRow[]): ProductPayload[] {
  return rows
    .filter((row) => row.name.trim().length > 0)
    .map((row) => ({
      name: row.name.trim(),
      product_url: (row.link ?? '').trim(),
      notes: (row.note ?? '').trim(),
    }));
}
